import { ArrowLeft, Search } from "lucide-react"
import { useMemo, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"

import { LoadingSpinner } from "@/components/shared"
import {
  CampaignDetailsSheet,
  CampaignLoadingSkeleton as CampaignItemSkeleton
} from "@/features/jobs/explore/components/campaign"
import { CampaignItem } from "@/features/jobs/explore/components/campaign-item"
import { FilterExplore } from "@/features/jobs/explore/components/explore-filter"
import { searchCampaign } from "@/features/jobs/search/api/search.api"
import { useInfiniteScrollQuery } from "@/modules/helpers/hooks"
import { useDebounce } from "@/modules/helpers/hooks/shared/use-debounce"
import {
  COMMISSION_RANGE_VALUE_MAP,
  DEFAULT_FILTER_EXPLORE,
  useFilterExplore
} from "@/modules/helpers/hooks/shared/use-filter-explore"
import { sendHaptic } from "@/modules/helpers/utils"
import { Campaign } from "@/modules/types"

const CampaignSearchPage = () => {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 300)
  const { filter } = useFilterExplore()
  const filterParams = useMemo(() => {
    if (!filter) return {}
    const params: Record<string, string | boolean | null> = {}

    if (filter.order !== DEFAULT_FILTER_EXPLORE.order) {
      params.order = filter.order
    }

    if (filter.isSelfFaceRequired !== DEFAULT_FILTER_EXPLORE.isSelfFaceRequired) {
      params.isSelfFaceRequired = filter.isSelfFaceRequired
    }

    if (filter.commissionRange !== DEFAULT_FILTER_EXPLORE.commissionRange) {
      const range = COMMISSION_RANGE_VALUE_MAP[filter.commissionRange]
      if (range?.startCommissionRate) {
        params.startCommissionRate = range.startCommissionRate
      }
      if (range?.endCommissionRate) {
        params.endCommissionRate = range.endCommissionRate
      }
    }

    return params
  }, [filter])

  const filterSignature = useMemo(() => JSON.stringify(filterParams), [filterParams])

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useInfiniteScrollQuery<unknown, Campaign>({
      queryKey: ["campaign-search", debouncedSearch, filterSignature],
      queryFn: searchCampaign,
      params: {
        search: debouncedSearch || undefined,
        ...filterParams
      }
    })

  const campaigns = data?.list || []

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header Search */}
      <div className="sticky top-0 z-50 flex items-center gap-2 p-4 border-b border-gray-200 bg-white">
        <button
          onClick={() => {
            window.history.back()
            sendHaptic()
          }}
          className="p-2 hover:bg-gray-100 rounded-full bg-primary-50"
        >
          <ArrowLeft className="w-5 h-5 text-primary" />
        </button>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Tìm kiếm sản phẩm..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 text-sm focus:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-800"
            autoFocus
          />
        </div>
        <FilterExplore />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" id="scrollable">
        {isLoading && (
          <div className="grid grid-cols-2 gap-2 p-2">
            {[...Array(6)].map((_, i) => (
              <CampaignItemSkeleton key={i} />
            ))}
          </div>
        )}

        {!isLoading && campaigns.length === 0 && (
          <div className="h-40 flex items-center justify-center">
            <p className="text-gray-400 text-sm">Không tìm thấy chiến dịch nào.</p>
          </div>
        )}

        <InfiniteScroll
          dataLength={campaigns.length}
          next={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
          hasMore={hasNextPage}
          loader={
            <div className="flex justify-center py-2">
              <LoadingSpinner className="w-6 h-6" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          <div className="grid grid-cols-2 gap-2 p-2">
            {campaigns.map((item) => (
              <CampaignItem key={item.id} data={item} />
            ))}
          </div>
        </InfiniteScroll>
        <CampaignDetailsSheet />
      </div>
    </div>
  )
}

export default CampaignSearchPage
